import React, { FC } from 'react';
import Button from '@mui/material/Button';
import cn from 'classnames';
import { useOrderPipelineAudit } from 'api/queries/useOrderPipelineAudit';
import styles from './OrderPipelineHistory.module.scss';
import { PipelineRunItem } from './PipelineRunItem';

interface OrderPipelineHistoryProps {
  serviceOrderId: number | string | undefined;
}

/**
 * Pipeline history for one service order, read from the structured `[Order Pipeline]` events the
 * backend logs to DataDog.
 *
 * Named "Pipeline history", not "Audit trail", on purpose: runs are reconstructed from event
 * timestamps rather than a run correlation id, and coverage is bounded by the DataDog retention
 * window — so it is an investigation aid, not a system of record.
 */
export const OrderPipelineHistory: FC<OrderPipelineHistoryProps> = ({ serviceOrderId }) => {
  const { data, isInitialLoading, isError, refetch, isFetching } =
    useOrderPipelineAudit(serviceOrderId);

  const renderBody = () => {
    /*
     * `isInitialLoading`, not `isLoading`: in react-query v4 a disabled query keeps
     * `status: 'loading'` with `fetchStatus: 'idle'`, so `isLoading` would leave a spinner up
     * forever when there is no order id to query.
     */
    if (isInitialLoading) {
      return <div className={styles.state}>Loading pipeline history…</div>;
    }

    /*
     * An unavailable audit source must never render as "nothing happened" — the operator would
     * conclude the order has no pipeline activity when we simply could not read it.
     */
    if (isError) {
      return (
        <div className={styles.errorState}>
          <span>Pipeline history is temporarily unavailable.</span>
          <Button size="small" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Retrying…' : 'Retry'}
          </Button>
        </div>
      );
    }

    // No data and nothing in flight — the query never ran (no order id).
    if (!data) {
      return <div className={styles.state}>Pipeline history is unavailable for this order.</div>;
    }

    if (!data.runs.length) {
      return (
        <div className={styles.state}>
          No pipeline events recorded in the last {data.window.retentionDays} days.
        </div>
      );
    }

    return (
      <div className={styles.runList}>
        {data.runs.map((run) => (
          <PipelineRunItem key={`${run.pipeline}-${run.startedAt}`} run={run} />
        ))}
      </div>
    );
  };

  return (
    <div className={cn('data-card-details-container', styles.card)}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className={styles.title}>Pipeline History</h3>
          {/*
            Retention is read from the response, never hardcoded — otherwise this copy silently
            drifts the day the DataDog retention setting changes. It sits under the title as a
            subtitle rather than beside it, so the title never has to compete for width.
          */}
          {data?.window.retentionDays ? (
            <p className={styles.coverage}>
              Showing pipeline events from the last {data.window.retentionDays} days
            </p>
          ) : null}
        </div>
        {/*
          Manual refresh, no polling. The search budget is 300 requests per minute org-wide, so an
          on-demand re-read is cheap — and the common question here is "did the change I just made
          show up yet?", which a static panel cannot answer.
        */}
        {data && !isError ? (
          <Button size="small" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        ) : null}
      </div>

      {data?.truncated ? (
        <p className={styles.warning}>
          More events exist than are shown here — this history was truncated.
        </p>
      ) : null}

      {renderBody()}
    </div>
  );
};
